<template>
  <div class="page-content">
    <breadcrumb-index
      :items="breadcrumbs"
      icon="trending-up"
      :badge="totalCount ? `${totalCount} insights` : null"
      badge-variant="badge-primary"
    >
      <template #actions>
        <button @click="openBulkModal" class="btn btn-outline-primary btn-sm me-2" :disabled="!canManage">
          <vue-feather type="copy" size="14" class="me-1" />
          Bulk Create
        </button>
        <button @click="openCreateModal" class="btn btn-primary btn-sm" :disabled="!canManage">
          <vue-feather type="plus" size="14" class="me-1" />
          Add Insight
        </button>
      </template>
    </breadcrumb-index>

    <!-- Cascading Filter Section -->
    <div class="filter-cascade-section">
      <div class="filter-cascade-header">
        <vue-feather type="filter" size="18" />
        <h4>Select Exam &amp; Scope to View Pattern Insights</h4>
      </div>
      <div class="filter-cascade-grid">
        <div class="filter-step" :class="{ active: true, completed: selectedExam }">
          <div class="step-number">1</div>
          <div class="step-content">
            <label class="step-label">Select Exam</label>
            <select v-model="selectedExam" class="filter-select" @change="onExamChange" :disabled="loadingExams">
              <option value="">{{ loadingExams ? 'Loading exams...' : 'Choose an exam...' }}</option>
              <option v-for="exam in exams" :key="exam.id" :value="exam.id">{{ exam.name }}</option>
            </select>
          </div>
        </div>
        <div class="filter-step" :class="{ active: selectedExam, completed: insightScope, disabled: !selectedExam }">
          <div class="step-number">2</div>
          <div class="step-content">
            <label class="step-label">Insight Scope</label>
            <div class="scope-toggle">
              <button
                :class="['scope-btn', { active: insightScope === 'exam' }]"
                @click="setScope('exam')"
                :disabled="!selectedExam"
              >
                <vue-feather type="book" size="13" class="me-1" />
                Exam-Level
              </button>
              <button
                :class="['scope-btn', { active: insightScope === 'subject' }]"
                @click="setScope('subject')"
                :disabled="!selectedExam"
              >
                <vue-feather type="layers" size="13" class="me-1" />
                Subject-Level
              </button>
            </div>
          </div>
        </div>
        <div v-if="insightScope === 'subject'" class="filter-step" :class="{ active: insightScope === 'subject', completed: selectedSubject, disabled: insightScope !== 'subject' }">
          <div class="step-number">3</div>
          <div class="step-content">
            <label class="step-label">Select Subject</label>
            <select v-model="selectedSubject" class="filter-select" :disabled="!selectedExam || loadingSubjects" @change="onSubjectChange">
              <option value="">{{ loadingSubjects ? 'Loading subjects...' : 'Choose a subject...' }}</option>
              <option v-for="subject in subjects" :key="subject.id" :value="subject.id">{{ subject.name }}</option>
            </select>
          </div>
        </div>
      </div>
    </div>

    <!-- Insights Content -->
    <div v-if="canManage" class="insights-content">
      <DataTable
        :items="filteredInsights"
        :columns="columns"
        :loading="loading"
        :paginated="false"
        search-placeholder="Search insights..."
        empty-icon="trending-up"
        empty-title="No pattern insights found"
        :empty-description="insightScope === 'exam' ? 'Add your first exam-level insight.' : 'Add your first insight for this subject.'"
      >
        <template #filters>
          <select v-model="filterType" class="filter-select-modern">
            <option value="">All Types</option>
            <option value="weightage">Weightage</option>
            <option value="trend">Trend</option>
            <option value="difficulty">Difficulty</option>
            <option value="tip">Tip</option>
            <option value="pyq">PYQ</option>
            <option value="cutoff">Cutoff</option>
          </select>
          <select v-model="filterActive" class="filter-select-modern">
            <option value="">All Status</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
        </template>

        <template #cell-order="{ item }">
          <div class="sort-handle" v-tooltip="'Drag to reorder'">
            <span class="sort-order-num">{{ item.sort_order }}</span>
          </div>
        </template>

        <template #cell-title="{ item }">
          <div class="avatar-info">
            <span class="avatar-name">{{ item.title }}</span>
            <span class="avatar-subtitle">{{ item.summary }}</span>
          </div>
        </template>

        <template #cell-type="{ item }">
          <span :class="['badge', getTypeBadge(item.type)]">
            <vue-feather :type="getTypeIcon(item.type)" size="11" class="me-1" />
            {{ item.type }}
          </span>
        </template>

        <template #cell-metric="{ item }">
          <div v-if="item.metric_value" class="metric-display">
            <span class="metric-value">{{ item.metric_value }}</span>
            <span v-if="item.metric_label" class="metric-label">{{ item.metric_label }}</span>
          </div>
          <span v-else class="text-muted">—</span>
        </template>

        <template #cell-status="{ item }">
          <span :class="['status-badge', item.is_active ? 'status-active' : 'status-inactive']">
            {{ item.is_active ? 'Active' : 'Inactive' }}
          </span>
        </template>

        <template #cell-actions="{ item }">
          <div class="action-buttons">
            <button class="action-btn action-view" @click="openDetailModal(item)" v-tooltip="'View detail'">
              <vue-feather type="eye" size="14" />
            </button>
            <button v-if="canEdit" class="action-btn" :class="item.is_active ? '' : ''" @click="toggleInsight(item)"
              v-tooltip="item.is_active ? 'Deactivate' : 'Activate'"
              :style="item.is_active ? 'color: #10b981;' : 'color: #9ca3af;'">
              <vue-feather :type="item.is_active ? 'toggle-right' : 'toggle-left'" size="14" />
            </button>
            <button v-if="canEdit" class="action-btn action-edit" @click="openEditModal(item)" v-tooltip="'Edit insight'">
              <vue-feather type="edit-2" size="14" />
            </button>
            <button v-if="canEdit" class="action-btn action-delete" @click="confirmDeleteInsight(item)" v-tooltip="{ text: 'Delete', variant: 'danger' }">
              <vue-feather type="trash-2" size="14" />
            </button>
          </div>
        </template>

        <template #empty-action>
          <button @click="openCreateModal" class="btn btn-primary">
            <vue-feather type="plus" size="16" class="me-1" />
            Add First Insight
          </button>
        </template>
      </DataTable>

      <!-- Reorder Section -->
      <div v-if="insights.length > 1" class="reorder-section">
        <div class="reorder-header">
          <vue-feather type="move" size="16" />
          <span>Reorder Insights</span>
        </div>
        <div class="reorder-list">
          <div v-for="(item, index) in orderedInsights" :key="item.id" class="reorder-item">
            <div class="reorder-handle">
              <span class="reorder-num">{{ index + 1 }}</span>
            </div>
            <div class="reorder-info">
              <span :class="['badge badge-sm', getTypeBadge(item.type)]">{{ item.type }}</span>
              <span class="reorder-title">{{ item.title }}</span>
            </div>
            <div class="reorder-actions">
              <button class="reorder-btn" :disabled="index === 0" @click="moveInsight(index, -1)" v-tooltip="'Move up'">
                <vue-feather type="chevron-up" size="14" />
              </button>
              <button class="reorder-btn" :disabled="index === orderedInsights.length - 1" @click="moveInsight(index, 1)" v-tooltip="'Move down'">
                <vue-feather type="chevron-down" size="14" />
              </button>
            </div>
          </div>
        </div>
        <button class="btn btn-primary btn-sm mt-2" @click="saveOrder" :disabled="savingOrder">
          <vue-feather :type="savingOrder ? 'loader' : 'save'" size="14" class="me-1" :class="{ spin: savingOrder }" />
          Save Order
        </button>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="select-prompt">
      <div class="select-prompt-icon">
        <vue-feather type="trending-up" size="64" />
      </div>
      <h3>{{ !selectedExam ? 'Select an Exam' : !insightScope ? 'Choose a Scope' : 'Select a Subject' }}</h3>
      <p>{{ !selectedExam ? 'Choose an exam from the filters above to get started.' : !insightScope ? 'Choose Exam-Level or Subject-Level scope to view insights.' : 'Choose a subject to view and manage subject-level pattern insights.' }}</p>
    </div>

    <!-- Create/Edit Modal -->
    <CreateModal
      v-model="showModal"
      :title="isEditing ? 'Edit Insight' : 'Add Insight'"
      icon="trending-up"
      size="lg"
      :loading="saving"
      :submit-text="isEditing ? 'Update' : 'Create'"
      :submit-disabled="!form.title || !form.summary || !form.detail || !form.type"
      :item-id="isEditing ? editingId : ''"
      @submit="saveInsight"
    >
      <form @submit.prevent="saveInsight" class="modal-form">
        <div class="form-group">
          <label class="form-label">Title <span class="text-danger">*</span></label>
          <input v-model="form.title" type="text" class="form-control" placeholder="e.g., High Weightage Subject" maxlength="80" required />
          <small class="text-muted">{{ form.title.length }}/80</small>
        </div>

        <!-- AI Enhance Button -->
        <div class="ai-enhance-section" v-if="form.title && form.type">
          <button type="button" @click="aiEnhanceInsight" class="ai-enhance-btn" :disabled="aiEnhancing">
            <vue-feather :type="aiEnhancing ? 'loader' : 'cpu'" size="16" :class="{ 'spin': aiEnhancing }" />
            {{ aiEnhancing ? 'Generating...' : 'AI Generate Insight' }}
          </button>
          <button type="button" @click="aiTranslateInsight" class="ai-translate-btn" :disabled="aiTranslating">
            <vue-feather :type="aiTranslating ? 'loader' : 'globe'" size="16" :class="{ 'spin': aiTranslating }" />
            {{ aiTranslating ? 'Translating...' : 'AI Translate' }}
          </button>
          <span class="ai-hint">Auto-fill summary, detail & metrics</span>
        </div>

        <!-- Translation Language Selector -->
        <div class="ai-translate-section" v-if="form.title && form.type && showTranslateOptions">
          <label class="form-label">Translate to</label>
          <select v-model="translateTargetLang" class="form-control form-control-sm" style="max-width: 200px;">
            <option v-for="lang in languageOptions.filter(l => l.code !== (form.language || 'en'))" :key="lang.code" :value="lang.code">{{ lang.label }}</option>
          </select>
          <button type="button" @click="confirmTranslateInsight" class="ai-translate-btn" :disabled="aiTranslating || !translateTargetLang">
            <vue-feather :type="aiTranslating ? 'loader' : 'globe'" size="16" :class="{ 'spin': aiTranslating }" />
            {{ aiTranslating ? 'Translating...' : 'Translate Now' }}
          </button>
        </div>

        <div class="form-group">
          <label class="form-label">Summary <span class="text-danger">*</span></label>
          <input v-model="form.summary" type="text" class="form-control" placeholder="Brief card description" maxlength="150" required />
          <small class="text-muted">{{ form.summary.length }}/150</small>
        </div>

        <div class="form-group">
          <label class="form-label">Detail <span class="text-danger">*</span></label>
          <textarea v-model="form.detail" class="form-control" rows="5" placeholder="Full analysis text..." maxlength="1000" required></textarea>
          <small class="text-muted">{{ form.detail.length }}/1000</small>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label class="form-label">Type <span class="text-danger">*</span></label>
            <select v-model="form.type" class="form-control" required>
              <option value="">Select type</option>
              <option value="weightage">Weightage</option>
              <option value="trend">Trend</option>
              <option value="difficulty">Difficulty</option>
              <option value="tip">Tip</option>
              <option value="pyq">PYQ</option>
              <option value="cutoff">Cutoff</option>
            </select>
          </div>
          <div class="form-group">
            <label class="form-label">Sort Order</label>
            <input v-model.number="form.sort_order" type="number" class="form-control" min="0" />
          </div>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label class="form-label">Metric Value</label>
            <input v-model="form.metric_value" type="text" class="form-control" placeholder="e.g., 27%" maxlength="20" />
          </div>
          <div class="form-group">
            <label class="form-label">Metric Label</label>
            <input v-model="form.metric_label" type="text" class="form-control" placeholder="e.g., avg. weightage" maxlength="40" />
          </div>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label class="form-label">Source</label>
            <input v-model="form.source" type="text" class="form-control" placeholder="e.g., NTA Official Papers" maxlength="100" />
          </div>
          <div class="form-group">
            <label class="form-label">Source Year</label>
            <input v-model.number="form.source_year" type="number" class="form-control" placeholder="e.g., 2025" />
          </div>
        </div>

        <div v-if="isEditing" class="form-group">
          <label class="form-label">Active</label>
          <select v-model="form.is_active" class="form-control">
            <option :value="true">Active</option>
            <option :value="false">Inactive</option>
          </select>
        </div>

        <div class="form-group">
          <label class="form-label">Language</label>
          <select v-model="form.language" class="form-control" @change="onLanguageChange">
            <option v-for="lang in languageOptions" :key="lang.code" :value="lang.code">{{ lang.label }}</option>
          </select>
          <small v-if="loadingTranslation" class="text-info"><vue-feather type="loader" size="12" class="spin" /> Loading translation...</small>
        </div>
      </form>
    </CreateModal>

    <!-- Detail View Modal -->
    <CreateModal
      v-model="showDetailModal"
      :title="detailItem?.title || 'Insight Detail'"
      icon="trending-up"
      size="lg"
      :show-default-footer="false"
    >
      <div v-if="detailItem" class="detail-view">
        <!-- Language Switch -->
        <div class="translate-bar">
          <div class="translate-bar-left">
            <vue-feather type="globe" size="16" />
            <label class="translate-label">View in:</label>
            <select v-model="detailSelectedLanguage" class="translate-select" @change="onDetailLanguageChange">
              <option value="">Original</option>
              <option v-for="lang in languageOptions" :key="lang.code" :value="lang.code">{{ lang.label }}</option>
            </select>
          </div>
          <div v-if="detailTranslating" class="translate-loading-inline">
            <vue-feather type="loader" size="14" class="spin" /> Loading...
          </div>
        </div>

        <div class="detail-meta">
          <span :class="['badge', getTypeBadge(detailItem.type)]">
            <vue-feather :type="getTypeIcon(detailItem.type)" size="11" class="me-1" />
            {{ detailItem.type }}
          </span>
          <span class="badge" :class="detailItem.subject_id ? 'bg-light text-dark' : 'bg-dark'">
            {{ detailItem.subject_id ? 'Subject-Level' : 'Exam-Level' }}
          </span>
          <span :class="['status-badge', detailItem.is_active ? 'status-active' : 'status-inactive']">
            {{ detailItem.is_active ? 'Active' : 'Inactive' }}
          </span>
        </div>
        <div v-if="detailItem.metric_value" class="detail-metric">
          <span class="detail-metric-value">{{ detailItem.metric_value }}</span>
          <span v-if="detailItem.metric_label" class="detail-metric-label">{{ detailItem.metric_label }}</span>
        </div>
        <p class="detail-summary">{{ detailDisplaySummary }}</p>
        <div class="detail-body">{{ detailDisplayDetail }}</div>
        <div v-if="detailItem.source" class="detail-source">
          <vue-feather type="info" size="12" />
          Source: {{ detailItem.source }}<span v-if="detailItem.source_year"> ({{ detailItem.source_year }})</span>
        </div>
      </div>
    </CreateModal>

    <!-- Bulk Create Modal -->
    <CreateModal
      v-model="showBulkModal"
      title="Bulk Create Insight"
      icon="copy"
      size="lg"
      :loading="savingBulk"
      submit-text="Create for Selected Targets"
      :submit-disabled="!bulkForm.title || !bulkForm.summary || !bulkForm.detail || !bulkForm.type || bulkTargets.length === 0"
      @submit="saveBulkInsights"
    >
      <form @submit.prevent="saveBulkInsights" class="modal-form">
        <div class="form-group">
          <label class="form-label">Target Exam-Subject Pairs <span class="text-danger">*</span></label>
          <p class="text-muted" style="font-size: 0.8rem; margin: 0;">Select exam first, then check subjects to target.</p>
          <div class="bulk-target-section">
            <select v-model="bulkExamId" class="form-control mb-2" @change="onBulkExamChange">
              <option value="">Select exam...</option>
              <option v-for="exam in exams" :key="exam.id" :value="exam.id">{{ exam.name }}</option>
            </select>
            <div v-if="bulkSubjects.length" class="exam-checkboxes">
              <label class="checkbox-item" @click.prevent="toggleAllBulkSubjects">
                <input type="checkbox" :checked="allBulkSubjectsSelected" />
                <span>Select All ({{ bulkSubjects.length }})</span>
              </label>
              <div class="exam-checkbox-list">
                <label v-for="sub in bulkSubjects" :key="sub.id" class="checkbox-item">
                  <input type="checkbox" :value="sub.id" v-model="bulkSelectedSubjects" />
                  <span>{{ sub.name }}</span>
                </label>
              </div>
            </div>
          </div>
          <small class="text-muted">{{ bulkTargets.length }} target(s) selected</small>
        </div>

        <div class="form-group">
          <label class="form-label">Title <span class="text-danger">*</span></label>
          <input v-model="bulkForm.title" type="text" class="form-control" placeholder="Insight title" maxlength="80" required />
        </div>

        <div class="form-group">
          <label class="form-label">Summary <span class="text-danger">*</span></label>
          <input v-model="bulkForm.summary" type="text" class="form-control" placeholder="Brief summary" maxlength="150" required />
        </div>

        <div class="form-group">
          <label class="form-label">Detail <span class="text-danger">*</span></label>
          <textarea v-model="bulkForm.detail" class="form-control" rows="4" placeholder="Full detail text..." maxlength="1000" required></textarea>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label class="form-label">Type <span class="text-danger">*</span></label>
            <select v-model="bulkForm.type" class="form-control" required>
              <option value="">Select type</option>
              <option value="weightage">Weightage</option>
              <option value="trend">Trend</option>
              <option value="difficulty">Difficulty</option>
              <option value="tip">Tip</option>
              <option value="pyq">PYQ</option>
              <option value="cutoff">Cutoff</option>
            </select>
          </div>
          <div class="form-group">
            <label class="form-label">Sort Order</label>
            <input v-model.number="bulkForm.sort_order" type="number" class="form-control" min="0" />
          </div>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label class="form-label">Metric Value</label>
            <input v-model="bulkForm.metric_value" type="text" class="form-control" placeholder="e.g., 60%" maxlength="20" />
          </div>
          <div class="form-group">
            <label class="form-label">Metric Label</label>
            <input v-model="bulkForm.metric_label" type="text" class="form-control" placeholder="e.g., easy questions" maxlength="40" />
          </div>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label class="form-label">Source</label>
            <input v-model="bulkForm.source" type="text" class="form-control" maxlength="100" />
          </div>
          <div class="form-group">
            <label class="form-label">Source Year</label>
            <input v-model.number="bulkForm.source_year" type="number" class="form-control" />
          </div>
        </div>

        <div class="form-group">
          <label class="form-label">Language</label>
          <select v-model="bulkForm.language" class="form-control">
            <option v-for="lang in languageOptions" :key="lang.code" :value="lang.code">{{ lang.label }}</option>
          </select>
        </div>
      </form>
    </CreateModal>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import api from '@/services/api'
import { useAuthStore } from '@/stores/auth'
import { useNotifications } from '@/composables/useNotifications'
import DataTable from '@/components/data-table/DataTable.vue'
import CreateModal from '@/components/modals/CreateModal.vue'
import { languageOptions } from '@/utils/languageOptions'

export default {
  name: 'PatternInsightsList',
  components: { DataTable, CreateModal },
  setup() {
    const authStore = useAuthStore()
    const { toast, confirmDelete: confirmDeleteAction } = useNotifications()
    const canEdit = computed(() => authStore.isSuperAdmin || authStore.isAdmin)

    const breadcrumbs = [
      { title: 'Dashboard', url: '/app/dashboard' },
      { title: 'Pattern Insights', active: true }
    ]

    const columns = [
      { key: 'order', label: '#', class: 'text-center', minWidth: '50px' },
      { key: 'title', label: 'Title', sortable: true, minWidth: '280px' },
      { key: 'type', label: 'Type', sortable: true },
      { key: 'metric', label: 'Metric' },
      { key: 'status', label: 'Status', sortable: true },
      { key: 'actions', label: 'Actions', class: 'text-end' }
    ]

    // Data
    const insights = ref([])
    const loading = ref(false)
    const totalCount = ref(0)

    // Cascading filters
    const exams = ref([])
    const subjects = ref([])
    const selectedExam = ref('')
    const selectedSubject = ref('')
    const insightScope = ref('') // 'exam' or 'subject'
    const loadingExams = ref(false)
    const loadingSubjects = ref(false)
    const filterType = ref('')
    const filterActive = ref('')

    // Modal state
    const showModal = ref(false)
    const showDetailModal = ref(false)
    const isEditing = ref(false)
    const saving = ref(false)
    const editingId = ref(null)
    const detailItem = ref(null)
    const aiEnhancing = ref(false)
    const aiTranslating = ref(false)
    const showTranslateOptions = ref(false)
    const translateTargetLang = ref('hi')
    const loadingTranslation = ref(false)

    const form = reactive({
      title: '', summary: '', detail: '', type: '',
      metric_value: '', metric_label: '',
      source: '', source_year: null,
      sort_order: 0, is_active: true,
      language: 'en'
    })

    // Bulk modal
    const showBulkModal = ref(false)
    const savingBulk = ref(false)
    const bulkExamId = ref('')
    const bulkSubjects = ref([])
    const bulkSelectedSubjects = ref([])

    const bulkForm = reactive({
      title: '', summary: '', detail: '', type: '',
      metric_value: '', metric_label: '',
      source: '', source_year: null, sort_order: 0,
      language: 'en'
    })

    // Reorder
    const orderedInsights = ref([])
    const savingOrder = ref(false)

    // Computed
    const canManage = computed(() => {
      if (!canEdit.value) return false
      if (!selectedExam.value || !insightScope.value) return false
      if (insightScope.value === 'exam') return true
      return !!selectedSubject.value
    })

    const filteredInsights = computed(() => {
      return insights.value.filter(i => {
        const matchesType = !filterType.value || i.type === filterType.value
        const matchesActive = filterActive.value === '' || String(i.is_active) === filterActive.value
        return matchesType && matchesActive
      })
    })

    const bulkTargets = computed(() => {
      if (!bulkExamId.value) return []
      return bulkSelectedSubjects.value.map(sid => ({
        exam_id: bulkExamId.value,
        subject_id: sid
      }))
    })

    const allBulkSubjectsSelected = computed(() =>
      bulkSubjects.value.length > 0 && bulkSelectedSubjects.value.length === bulkSubjects.value.length
    )

    // Fetch exams
    const fetchExams = async () => {
      loadingExams.value = true
      try {
        const res = await api.get('/v1/admin/exams')
        exams.value = res.data.exams || []
      } catch (err) {
        console.error('Failed to fetch exams:', err)
        toast.error('Failed to load exams')
      } finally {
        loadingExams.value = false
      }
    }

    // Fetch subjects for exam
    const fetchSubjects = async (examId) => {
      if (!examId) { subjects.value = []; return }
      loadingSubjects.value = true
      try {
        const res = await api.get(`/v1/admin/exams/${examId}/subjects`)
        subjects.value = res.data.subjects || []
      } catch (err) {
        console.error('Failed to fetch subjects:', err)
        subjects.value = []
      } finally {
        loadingSubjects.value = false
      }
    }

    // Fetch insights
    const fetchInsights = async () => {
      if (!selectedExam.value || !insightScope.value) {
        insights.value = []
        orderedInsights.value = []
        totalCount.value = 0
        return
      }
      if (insightScope.value === 'subject' && !selectedSubject.value) {
        insights.value = []
        orderedInsights.value = []
        totalCount.value = 0
        return
      }
      loading.value = true
      try {
        const params = { examId: selectedExam.value }
        if (insightScope.value === 'exam') {
          params.subjectId = 'exam'
        } else {
          params.subjectId = selectedSubject.value
        }
        const res = await api.get('/v1/admin/pattern-insights', { params })
        const data = res.data.insights || []
        insights.value = data.slice().sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
        orderedInsights.value = [...insights.value]
        totalCount.value = res.data.total || data.length
      } catch (err) {
        console.error('Failed to fetch insights:', err)
        toast.error('Failed to load insights')
        insights.value = []
        orderedInsights.value = []
        totalCount.value = 0
      } finally {
        loading.value = false
      }
    }

    const onExamChange = async () => {
      selectedSubject.value = ''
      insightScope.value = ''
      subjects.value = []
      insights.value = []
      orderedInsights.value = []
      totalCount.value = 0
      await fetchSubjects(selectedExam.value)
    }

    const setScope = (scope) => {
      insightScope.value = scope
      selectedSubject.value = ''
      filterType.value = ''
      filterActive.value = ''
      if (scope === 'exam') {
        fetchInsights()
      } else {
        insights.value = []
        orderedInsights.value = []
        totalCount.value = 0
      }
    }

    const onSubjectChange = () => {
      filterType.value = ''
      filterActive.value = ''
      fetchInsights()
    }

    // Form helpers
    const resetForm = () => {
      form.title = ''; form.summary = ''; form.detail = ''; form.type = ''
      form.metric_value = ''; form.metric_label = ''
      form.source = ''; form.source_year = null
      form.sort_order = 0; form.is_active = true
      form.language = 'en'
      editingId.value = null; isEditing.value = false
    }

    const resetBulkForm = () => {
      bulkForm.title = ''; bulkForm.summary = ''; bulkForm.detail = ''; bulkForm.type = ''
      bulkForm.metric_value = ''; bulkForm.metric_label = ''
      bulkForm.source = ''; bulkForm.source_year = null; bulkForm.sort_order = 0
      bulkForm.language = 'en'
      bulkExamId.value = ''; bulkSubjects.value = []; bulkSelectedSubjects.value = []
    }

    const openCreateModal = () => {
      if (!canManage.value) return
      resetForm()
      showModal.value = true
    }

    const openEditModal = (item) => {
      isEditing.value = true
      editingId.value = item.id
      form.title = item.title || ''
      form.summary = item.summary || ''
      form.detail = item.detail || ''
      form.type = item.type || ''
      form.metric_value = item.metric_value || ''
      form.metric_label = item.metric_label || ''
      form.source = item.source || ''
      form.source_year = item.source_year || null
      form.sort_order = item.sort_order || 0
      form.is_active = item.is_active
      form.language = item.language || 'en'
      showModal.value = true
    }

    const openDetailModal = (item) => {
      detailItem.value = item
      detailSelectedLanguage.value = ''
      detailTranslatedContent.value = null
      showDetailModal.value = true
    }

    // Detail modal translation state
    const detailSelectedLanguage = ref('')
    const detailTranslating = ref(false)
    const detailTranslatedContent = ref(null)

    const onDetailLanguageChange = async () => {
      detailTranslatedContent.value = null
      if (!detailSelectedLanguage.value || !detailItem.value?.id) return
      detailTranslating.value = true
      try {
        const res = await api.get('/v1/admin/translations', {
          params: { content_type: 'pattern_insight', content_id: detailItem.value.id, language_code: detailSelectedLanguage.value }
        })
        const translations = res.data?.translations || []
        if (translations.length > 0) {
          let td = translations[0].translated_data
          if (typeof td === 'string') { try { td = JSON.parse(td) } catch (e) { td = {} } }
          if (td.title || td.summary || td.detail) {
            detailTranslatedContent.value = td
          }
        }
      } catch (error) {
        console.error('Failed to fetch translation:', error)
      } finally {
        detailTranslating.value = false
      }
    }

    const detailDisplaySummary = computed(() => {
      if (detailSelectedLanguage.value && detailTranslatedContent.value?.summary) {
        return detailTranslatedContent.value.summary
      }
      return detailItem.value?.summary || ''
    })

    const detailDisplayDetail = computed(() => {
      if (detailSelectedLanguage.value && detailTranslatedContent.value?.detail) {
        return detailTranslatedContent.value.detail
      }
      return detailItem.value?.detail || ''
    })

    const openBulkModal = () => {
      resetBulkForm()
      showBulkModal.value = true
    }

    const onBulkExamChange = async () => {
      bulkSelectedSubjects.value = []
      bulkSubjects.value = []
      if (!bulkExamId.value) return
      try {
        const res = await api.get(`/v1/admin/exams/${bulkExamId.value}/subjects`)
        bulkSubjects.value = res.data.subjects || []
      } catch (err) {
        console.error('Failed to fetch subjects for bulk:', err)
      }
    }

    const toggleAllBulkSubjects = () => {
      if (allBulkSubjectsSelected.value) {
        bulkSelectedSubjects.value = []
      } else {
        bulkSelectedSubjects.value = bulkSubjects.value.map(s => s.id)
      }
    }

    // CRUD
    const saveInsight = async () => {
      if (!form.title || !form.summary || !form.detail || !form.type) return
      saving.value = true
      try {
        const payload = {
          title: form.title, summary: form.summary, detail: form.detail, type: form.type,
          metric_value: form.metric_value || null, metric_label: form.metric_label || null,
          source: form.source || null, source_year: form.source_year || null,
          sort_order: form.sort_order,
          language_code: form.language
        }
        if (isEditing.value) {
          payload.is_active = form.is_active
          await api.put(`/v1/admin/pattern-insights/${editingId.value}`, payload)
          toast.success('Insight updated')
        } else {
          payload.exam_id = selectedExam.value
          if (insightScope.value === 'subject' && selectedSubject.value) {
            payload.subject_id = selectedSubject.value
          }
          await api.post('/v1/admin/pattern-insights', payload)
          toast.success('Insight created')
        }
        showModal.value = false
        resetForm()
        fetchInsights()
      } catch (error) {
        toast.error(error.response?.data?.error?.message || 'Failed to save insight')
      } finally {
        saving.value = false
      }
    }

    const saveBulkInsights = async () => {
      if (!bulkForm.title || !bulkForm.summary || !bulkForm.detail || !bulkForm.type || bulkTargets.value.length === 0) return
      savingBulk.value = true
      try {
        const payload = {
          targets: bulkTargets.value,
          title: bulkForm.title, summary: bulkForm.summary, detail: bulkForm.detail, type: bulkForm.type,
          metric_value: bulkForm.metric_value || null, metric_label: bulkForm.metric_label || null,
          source: bulkForm.source || null, source_year: bulkForm.source_year || null,
          sort_order: bulkForm.sort_order,
          language: bulkForm.language
        }
        const res = await api.post('/v1/admin/pattern-insights/bulk', payload)
        toast.success(`Created ${res.data.createdCount || bulkTargets.value.length} insight(s)`)
        showBulkModal.value = false
        resetBulkForm()
        fetchInsights()
      } catch (error) {
        toast.error(error.response?.data?.error?.message || 'Failed to bulk create insights')
      } finally {
        savingBulk.value = false
      }
    }

    // AI Enhancement
    const parseAIInsightResponse = (response) => {
      if (response.data?.structured_data?.data) return response.data.structured_data.data
      const responseText = response.data?.response || response.data?.message || ''
      const jsonMatch = responseText.match(/```(?:json)?\s*([\s\S]*?)```/)
      if (jsonMatch) {
        try { return JSON.parse(jsonMatch[1].trim()) } catch (e) {}
      }
      try { return JSON.parse(responseText) } catch (e) {}
      return {}
    }

    const aiEnhanceInsight = async () => {
      if (!form.title || !form.type) {
        toast.error('Please enter a title and select a type first')
        return
      }
      aiEnhancing.value = true
      try {
        const examName = exams.value.find(e => e.id === selectedExam.value)?.name || 'the exam'
        const subjectName = insightScope.value === 'subject'
          ? (subjects.value.find(s => s.id === selectedSubject.value)?.name || '')
          : ''
        const scopeDesc = subjectName ? `Subject: "${subjectName}"` : 'Exam-level insight'

        const response = await api.post('/v1/admin/admin-chat', {
          message: `Generate a pattern insight for an educational exam preparation platform.\n\nExam: "${examName}"\n${scopeDesc}\nTitle: "${form.title}"\nType: ${form.type}\n\nProvide your response as a valid JSON object:\n{\n  "summary": "Brief card description (max 150 chars)",\n  "detail": "Full analysis text with data-driven observations (max 1000 chars)",\n  "metric_value": "e.g., 27% or 3/5",\n  "metric_label": "e.g., avg. weightage"\n}\n\nMake it data-driven and educational. Do not wrap in markdown.`,
          generation_type: 'insight_enhance',
          output_format: 'structured'
        })
        const aiData = parseAIInsightResponse(response)
        let fieldsUpdated = 0
        if (aiData.summary) { form.summary = String(aiData.summary).substring(0, 150); fieldsUpdated++ }
        if (aiData.detail) { form.detail = String(aiData.detail).substring(0, 1000); fieldsUpdated++ }
        if (aiData.metric_value) { form.metric_value = String(aiData.metric_value).substring(0, 20); fieldsUpdated++ }
        if (aiData.metric_label) { form.metric_label = String(aiData.metric_label).substring(0, 40); fieldsUpdated++ }
        if (aiData.source) { form.source = String(aiData.source).substring(0, 100); fieldsUpdated++ }
        if (fieldsUpdated > 0) toast.success(`AI filled ${fieldsUpdated} field(s)`)
        else toast.warning('AI responded but could not extract structured data.')
      } catch (error) {
        console.error('AI enhancement failed:', error)
        toast.error('Failed to generate insight with AI.')
      } finally {
        aiEnhancing.value = false
      }
    }

    // AI Translation
    const aiTranslateInsight = () => {
      if (!form.title && !form.summary) {
        toast.error('Please enter a title or summary first')
        return
      }
      showTranslateOptions.value = !showTranslateOptions.value
    }

    const confirmTranslateInsight = async () => {
      if ((!form.title && !form.summary) || !translateTargetLang.value) return
      const targetLangLabel = languageOptions.find(l => l.code === translateTargetLang.value)?.label || translateTargetLang.value
      aiTranslating.value = true
      try {
        const fieldsToTranslate = {}
        if (form.title) fieldsToTranslate.title = form.title
        if (form.summary) fieldsToTranslate.summary = form.summary
        if (form.detail) fieldsToTranslate.detail = form.detail
        if (form.metric_label) fieldsToTranslate.metric_label = form.metric_label

        const response = await api.post('/v1/admin/admin-chat', {
          message: `Translate the following educational insight content to ${targetLangLabel}. Keep metric values and technical terms intact. Return ONLY a valid JSON object with the same keys translated:\n\n${JSON.stringify(fieldsToTranslate, null, 2)}`,
          generation_type: 'translate',
          output_format: 'structured'
        })
        const aiData = parseAIInsightResponse(response)
        let fieldsUpdated = 0
        if (aiData.title) { form.title = aiData.title; fieldsUpdated++ }
        if (aiData.summary) { form.summary = String(aiData.summary).substring(0, 150); fieldsUpdated++ }
        if (aiData.detail) { form.detail = String(aiData.detail).substring(0, 1000); fieldsUpdated++ }
        if (aiData.metric_label) { form.metric_label = String(aiData.metric_label).substring(0, 40); fieldsUpdated++ }
        if (fieldsUpdated > 0) {
          toast.success(`${fieldsUpdated} field(s) translated to ${targetLangLabel}`)
        } else {
          toast.warning('AI responded but could not extract translated data.')
        }
        showTranslateOptions.value = false
      } catch (error) {
        console.error('AI translation failed:', error)
        toast.error('Failed to translate insight with AI.')
      } finally {
        aiTranslating.value = false
      }
    }

    // Fetch translation when language changes in edit mode
    const onLanguageChange = async () => {
      if (!isEditing.value || !editingId.value) return
      loadingTranslation.value = true
      try {
        const res = await api.get('/v1/admin/translations', {
          params: { content_type: 'pattern_insight', content_id: editingId.value, language_code: form.language }
        })
        const translations = res.data?.translations || []
        if (translations.length > 0) {
          let td = translations[0].translated_data
          if (typeof td === 'string') { try { td = JSON.parse(td) } catch (e) { td = {} } }
          if (td.title) form.title = td.title
          if (td.summary) form.summary = td.summary
          if (td.detail) form.detail = td.detail
        }
      } catch (error) {
        console.error('Failed to fetch translation:', error)
      } finally {
        loadingTranslation.value = false
      }
    }

    const toggleInsight = async (item) => {
      try {
        const res = await api.patch(`/v1/admin/pattern-insights/${item.id}/toggle`)
        const updated = res.data.insight
        const idx = insights.value.findIndex(i => i.id === item.id)
        if (idx !== -1 && updated) {
          insights.value[idx] = { ...insights.value[idx], ...updated }
        } else {
          fetchInsights()
        }
        toast.success(`Insight ${updated?.is_active ? 'activated' : 'deactivated'}`)
      } catch (error) {
        toast.error('Failed to toggle insight')
      }
    }

    const confirmDeleteInsight = async (item) => {
      const confirmed = await confirmDeleteAction(item.title)
      if (confirmed) {
        try {
          await api.delete(`/v1/admin/pattern-insights/${item.id}`)
          insights.value = insights.value.filter(i => i.id !== item.id)
          orderedInsights.value = orderedInsights.value.filter(i => i.id !== item.id)
          totalCount.value = Math.max(0, totalCount.value - 1)
          toast.success('Insight deleted')
        } catch (error) {
          toast.error('Failed to delete insight')
        }
      }
    }

    // Reorder
    const moveInsight = (index, direction) => {
      const newIndex = index + direction
      if (newIndex < 0 || newIndex >= orderedInsights.value.length) return
      const arr = [...orderedInsights.value]
      const temp = arr[index]
      arr[index] = arr[newIndex]
      arr[newIndex] = temp
      orderedInsights.value = arr
    }

    const saveOrder = async () => {
      savingOrder.value = true
      try {
        const payload = {
          exam_id: selectedExam.value,
          ordered_ids: orderedInsights.value.map(i => i.id)
        }
        if (insightScope.value === 'exam') {
          payload.subject_id = 'exam'
        } else {
          payload.subject_id = selectedSubject.value
        }
        await api.put('/v1/admin/pattern-insights/reorder', payload)
        toast.success('Order saved')
        fetchInsights()
      } catch (error) {
        toast.error('Failed to save order')
      } finally {
        savingOrder.value = false
      }
    }

    // Type helpers
    const getTypeBadge = (type) => {
      const map = {
        weightage: 'bg-primary', trend: 'bg-success', difficulty: 'bg-warning text-dark',
        tip: 'badge-purple', pyq: 'badge-pink', cutoff: 'badge-cyan'
      }
      return map[type] || 'bg-secondary'
    }

    const getTypeIcon = (type) => {
      const map = {
        weightage: 'pie-chart', trend: 'trending-up', difficulty: 'activity',
        tip: 'zap', pyq: 'file-text', cutoff: 'bar-chart'
      }
      return map[type] || 'info'
    }

    onMounted(fetchExams)

    return {
      breadcrumbs, columns, insights, loading, totalCount,
      exams, subjects, selectedExam, selectedSubject, insightScope, canManage, canEdit,
      loadingExams, loadingSubjects, filterType, filterActive, filteredInsights,
      showModal, showDetailModal, isEditing, saving, editingId, form, detailItem, aiEnhancing,
      aiTranslating, showTranslateOptions, translateTargetLang,
      loadingTranslation, onLanguageChange,
      detailSelectedLanguage, detailTranslating, detailTranslatedContent,
      onDetailLanguageChange, detailDisplaySummary, detailDisplayDetail,
      showBulkModal, savingBulk, bulkForm, bulkExamId, bulkSubjects, bulkSelectedSubjects,
      bulkTargets, allBulkSubjectsSelected,
      orderedInsights, savingOrder,
      onExamChange, setScope, onSubjectChange, fetchInsights,
      openCreateModal, openEditModal, openDetailModal, openBulkModal,
      onBulkExamChange, toggleAllBulkSubjects,
      saveInsight, saveBulkInsights, toggleInsight, confirmDeleteInsight,
      aiEnhanceInsight, aiTranslateInsight, confirmTranslateInsight,
      moveInsight, saveOrder,
      getTypeBadge, getTypeIcon,
      languageOptions
    }
  }
}
</script>

<style scoped>
/* Filter Section */
.filter-cascade-section {
  background: linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%);
  border-radius: 16px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border: 1px solid #c4b5fd;
}
.filter-cascade-header { display: flex; align-items: center; gap: 0.75rem; margin-bottom: 1.25rem; color: #7c3aed; }
.filter-cascade-header h4 { margin: 0; font-size: 1rem; font-weight: 600; }
.filter-cascade-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 1rem; }
.filter-step { display: flex; gap: 1rem; padding: 1rem; background: white; border-radius: 12px; border: 2px solid #e0e7ff; transition: all 0.3s ease; opacity: 0.6; }
.filter-step.active { opacity: 1; border-color: #8b5cf6; }
.filter-step.completed { border-color: #8b5cf6; background: linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%); }
.filter-step.disabled { pointer-events: none; opacity: 0.4; }
.step-number { width: 32px; height: 32px; border-radius: 50%; background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%); color: white; display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 0.875rem; flex-shrink: 0; }
.step-content { flex: 1; display: flex; flex-direction: column; gap: 0.5rem; }
.step-label { font-size: 0.75rem; font-weight: 600; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; }
.filter-select { padding: 0.625rem 0.875rem; border: 1px solid #d1d5db; border-radius: 8px; font-size: 0.875rem; background: white; cursor: pointer; transition: all 0.2s; }
.filter-select:focus { outline: none; border-color: #8b5cf6; box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1); }
.filter-select:disabled { background: #f3f4f6; cursor: not-allowed; }

/* Scope Toggle */
.scope-toggle { display: flex; gap: 0.5rem; }
.scope-btn { display: flex; align-items: center; padding: 0.5rem 0.875rem; border: 2px solid #d1d5db; border-radius: 8px; background: white; font-size: 0.8rem; font-weight: 500; color: #6b7280; cursor: pointer; transition: all 0.2s; }
.scope-btn:hover:not(:disabled) { border-color: #8b5cf6; color: #7c3aed; }
.scope-btn.active { border-color: #8b5cf6; background: linear-gradient(135deg, #f5f3ff, #ede9fe); color: #7c3aed; font-weight: 600; }
.scope-btn:disabled { opacity: 0.4; cursor: not-allowed; }

/* Empty State */
.select-prompt { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 4rem 2rem; text-align: center; background: #f9fafb; border-radius: 16px; border: 2px dashed #d1d5db; }
.select-prompt-icon { color: #9ca3af; margin-bottom: 1.5rem; }
.select-prompt h3 { margin: 0 0 0.5rem; font-size: 1.25rem; color: #374151; }
.select-prompt p { margin: 0; color: #6b7280; max-width: 400px; }

.insights-content { animation: fadeIn 0.3s ease; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }

/* Sort Handle */
.sort-handle { display: flex; align-items: center; justify-content: center; }
.sort-order-num { width: 28px; height: 28px; border-radius: 6px; background: #f3f4f6; display: flex; align-items: center; justify-content: center; font-size: 0.75rem; font-weight: 600; color: #6b7280; }

/* Metric Display */
.metric-display { display: flex; flex-direction: column; gap: 2px; }
.metric-value { font-weight: 700; font-size: 0.95rem; color: #7c3aed; }
.metric-label { font-size: 0.7rem; color: #9ca3af; }

/* Custom badge colors */
.badge-purple { background: #8b5cf6; color: white; }
.badge-pink { background: #ec4899; color: white; }
.badge-cyan { background: #06b6d4; color: white; }

/* Reorder Section */
.reorder-section { margin-top: 1.5rem; background: #f9fafb; border-radius: 12px; padding: 1.25rem; border: 1px solid #e5e7eb; }
.reorder-header { display: flex; align-items: center; gap: 0.5rem; font-weight: 600; font-size: 0.875rem; color: #374151; margin-bottom: 1rem; }
.reorder-list { display: flex; flex-direction: column; gap: 0.5rem; }
.reorder-item { display: flex; align-items: center; gap: 0.75rem; padding: 0.625rem 0.75rem; background: white; border-radius: 8px; border: 1px solid #e5e7eb; transition: box-shadow 0.2s; }
.reorder-item:hover { box-shadow: 0 2px 8px rgba(0,0,0,0.06); }
.reorder-handle { flex-shrink: 0; }
.reorder-num { width: 24px; height: 24px; border-radius: 50%; background: linear-gradient(135deg, #8b5cf6, #7c3aed); color: white; display: flex; align-items: center; justify-content: center; font-size: 0.7rem; font-weight: 700; }
.reorder-info { flex: 1; display: flex; align-items: center; gap: 0.5rem; min-width: 0; }
.reorder-title { font-size: 0.85rem; color: #374151; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.reorder-actions { display: flex; gap: 0.25rem; }
.reorder-btn { width: 28px; height: 28px; border: 1px solid #d1d5db; border-radius: 6px; background: white; cursor: pointer; display: flex; align-items: center; justify-content: center; color: #6b7280; transition: all 0.15s; }
.reorder-btn:hover:not(:disabled) { background: #f3f4f6; color: #374151; }
.reorder-btn:disabled { opacity: 0.3; cursor: not-allowed; }

/* Translate Bar */
.translate-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border-radius: 10px;
  border: 1px solid #7dd3fc;
}

.translate-bar-left {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #0369a1;
}

.translate-label {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #0369a1;
}

.translate-select {
  padding: 0.375rem 0.625rem;
  border: 1px solid #bae6fd;
  border-radius: 6px;
  font-size: 0.8125rem;
  background: white;
  cursor: pointer;
  min-width: 140px;
}

.translate-select:focus {
  outline: none;
  border-color: #0ea5e9;
  box-shadow: 0 0 0 2px rgba(14, 165, 233, 0.15);
}

.translate-loading-inline {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.8125rem;
  color: #0369a1;
}

:global(body.dark-mode) .translate-bar {
  background: linear-gradient(135deg, #0c4a6e 0%, #075985 100%);
  border-color: #0369a1;
}

:global(body.dark-mode) .translate-bar-left { color: #7dd3fc; }
:global(body.dark-mode) .translate-label { color: #7dd3fc; }
:global(body.dark-mode) .translate-select { background: #1e293b; border-color: #334155; color: #f3f4f6; }

/* Detail View */
.detail-view { display: flex; flex-direction: column; gap: 1rem; }
.detail-meta { display: flex; gap: 0.5rem; align-items: center; }
.detail-metric { display: flex; align-items: baseline; gap: 0.5rem; padding: 0.75rem 1rem; background: linear-gradient(135deg, #f5f3ff, #ede9fe); border-radius: 10px; }
.detail-metric-value { font-size: 1.75rem; font-weight: 800; color: #7c3aed; }
.detail-metric-label { font-size: 0.85rem; color: #6b7280; }
.detail-summary { font-size: 0.95rem; color: #374151; font-weight: 500; margin: 0; }
.detail-body { font-size: 0.875rem; color: #4b5563; line-height: 1.7; white-space: pre-wrap; }
.detail-source { display: flex; align-items: center; gap: 0.375rem; font-size: 0.8rem; color: #9ca3af; padding-top: 0.5rem; border-top: 1px solid #e5e7eb; }

/* Form */
.modal-form { display: flex; flex-direction: column; gap: 1rem; }
.form-group { display: flex; flex-direction: column; gap: 0.5rem; }
.form-label { font-weight: 500; font-size: 0.875rem; color: #374151; }
.form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }
.form-control { padding: 0.625rem 0.875rem; border: 1px solid #d1d5db; border-radius: 8px; font-size: 0.875rem; transition: border-color 0.2s, box-shadow 0.2s; }
.form-control:focus { outline: none; border-color: #8b5cf6; box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1); }

/* Bulk target */
.bulk-target-section { margin-top: 0.5rem; }
.exam-checkboxes { border: 1px solid #d1d5db; border-radius: 8px; overflow: hidden; }
.exam-checkbox-list { max-height: 180px; overflow-y: auto; padding: 0.25rem 0; }
.checkbox-item { display: flex; align-items: center; gap: 0.5rem; padding: 0.5rem 0.75rem; cursor: pointer; font-size: 0.875rem; transition: background 0.15s; }
.checkbox-item:hover { background: #f3f4f6; }
.checkbox-item:first-child { border-bottom: 1px solid #e5e7eb; background: #f9fafb; font-weight: 500; }

.mb-2 { margin-bottom: 0.5rem; }
.mt-2 { margin-top: 0.5rem; }
.badge-sm { font-size: 0.65rem; padding: 0.2rem 0.5rem; }

.spin { animation: spin 1s linear infinite; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

/* Dark mode */
:global(body.dark-mode) .filter-cascade-section { background: linear-gradient(135deg, #1e1b4b 0%, #0f172a 100%); border-color: #4c1d95; }
:global(body.dark-mode) .filter-cascade-header { color: #a78bfa; }
:global(body.dark-mode) .filter-step { background: #1e293b; border-color: #334155; }
:global(body.dark-mode) .filter-step.completed { background: linear-gradient(135deg, #1e1b4b 0%, #1e293b 100%); }
:global(body.dark-mode) .filter-select { background: #1e293b; border-color: #374151; color: #f3f4f6; }
:global(body.dark-mode) .scope-btn { background: #1e293b; border-color: #374151; color: #9ca3af; }
:global(body.dark-mode) .scope-btn.active { border-color: #8b5cf6; background: linear-gradient(135deg, #1e1b4b, #312e81); color: #a78bfa; }
:global(body.dark-mode) .select-prompt { background: #111827; border-color: #374151; }
:global(body.dark-mode) .select-prompt h3 { color: #f3f4f6; }
:global(body.dark-mode) .select-prompt p { color: #9ca3af; }
:global(body.dark-mode) .form-label { color: #d1d5db; }
:global(body.dark-mode) .form-control { background: #1e293b; border-color: #374151; color: #f3f4f6; }
:global(body.dark-mode) .reorder-section { background: #111827; border-color: #1e293b; }
:global(body.dark-mode) .reorder-item { background: #1e293b; border-color: #334155; }
:global(body.dark-mode) .reorder-title { color: #e5e7eb; }
:global(body.dark-mode) .reorder-btn { background: #1e293b; border-color: #374151; color: #9ca3af; }
:global(body.dark-mode) .sort-order-num { background: #334155; color: #d1d5db; }
:global(body.dark-mode) .detail-metric { background: linear-gradient(135deg, #1e1b4b, #312e81); }
:global(body.dark-mode) .detail-summary { color: #e5e7eb; }
:global(body.dark-mode) .detail-body { color: #d1d5db; }
:global(body.dark-mode) .detail-source { border-color: #334155; }
:global(body.dark-mode) .exam-checkboxes { border-color: #374151; }
:global(body.dark-mode) .checkbox-item:hover { background: #1e293b; }
:global(body.dark-mode) .checkbox-item:first-child { background: #0f172a; border-color: #334155; }

/* AI Enhance Section */
.ai-enhance-section {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 1rem;
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border-radius: 10px;
  border: 1px solid #fbbf24;
  flex-wrap: wrap;
}

.ai-enhance-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.ai-enhance-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.4);
}

.ai-enhance-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.ai-translate-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.ai-translate-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(14, 165, 233, 0.4);
}

.ai-translate-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.ai-translate-section {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%);
  border-radius: 10px;
  border: 1px solid #7dd3fc;
}

.ai-hint {
  font-size: 0.8rem;
  color: #92400e;
}

:global(body.dark-mode) .ai-enhance-section { background: linear-gradient(135deg, #78350f 0%, #92400e 100%); border-color: #b45309; }
:global(body.dark-mode) .ai-translate-section { background: linear-gradient(135deg, #0c4a6e 0%, #075985 100%); border-color: #0369a1; }
:global(body.dark-mode) .ai-hint { color: #fbbf24; }
</style>
